import { useState, useEffect, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import { resolveImageUrl } from "@/utils/imageUrl";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Monitor, Smartphone, Tablet, Shield, Trash2, Pencil, Check, X,
  Loader2, ArrowLeft, Globe, Clock, Camera, User, Mail, Bell, BarChart3,
} from "lucide-react";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function getDeviceIcon(browser, os) {
  const o = (os || "").toLowerCase();
  if (o.includes("android") || o.includes("ios") || o.includes("iphone")) return Smartphone;
  if (o.includes("ipad") || (browser || "").toLowerCase().includes("tablet")) return Tablet;
  return Monitor;
}

function formatDate(dateStr) {
  if (!dateStr) return "—";
  try {
    const d = new Date(dateStr);
    return d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }) +
      " at " + d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });
  } catch { return dateStr; }
}

export default function MyDevicesPage() {
  const { user, token, setUser } = useAuth();
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState("");
  const [actionLoading, setActionLoading] = useState(null);
  const [avatarUploading, setAvatarUploading] = useState(false);
  const [profileEditing, setProfileEditing] = useState(false);
  const [profileForm, setProfileForm] = useState({ name: "", phone: "" });
  const [profileSaving, setProfileSaving] = useState(false);
  const [emailPrefs, setEmailPrefs] = useState({ order_updates: true, promotional: true, quiz_results: true });
  const fileInputRef = useRef(null);

  const headers = { Authorization: `Bearer ${token}` };

  const fetchDevices = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/auth/my-devices`, { headers });
      setDevices(res.data);
    } catch {
      toast.error("Failed to load devices");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchDevices(); }, [fetchDevices]);

  useEffect(() => {
    if (!token) return;
    axios.get(`${API}/user/email-preferences`, { headers }).then(res => setEmailPrefs(res.data)).catch(() => {});
  }, [token]);

  const handleRename = async (deviceId) => {
    if (!editName.trim()) return;
    setActionLoading(deviceId);
    try {
      await axios.put(`${API}/auth/devices/${deviceId}/rename`, { nickname: editName.trim() }, { headers });
      toast.success("Device renamed");
      setEditingId(null);
      setEditName("");
      fetchDevices();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to rename device");
    } finally {
      setActionLoading(null);
    }
  };

  const handleRemove = async (device) => {
    if (!window.confirm(`Remove "${device.nickname || device.browser || "this device"}"? Any active session on it will be revoked.`)) return;
    setActionLoading(device.id);
    try {
      await axios.delete(`${API}/auth/devices/${device.id}`, { headers });
      toast.success("Device removed");
      fetchDevices();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to remove device");
    } finally {
      setActionLoading(null);
    }
  };

  const startEditing = (device) => {
    setEditingId(device.id);
    setEditName(device.nickname || "");
  };

  const handleAvatarUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) {
      toast.error("Image must be under 2MB");
      return;
    }
    setAvatarUploading(true);
    const formData = new FormData();
    formData.append("file", file);
    try {
      const res = await axios.post(`${API}/user/upload-avatar`, formData, {
        headers: { ...headers, "Content-Type": "multipart/form-data" },
      });
      setUser(prev => ({ ...prev, picture: res.data.picture }));
      toast.success("Profile picture updated!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to upload picture");
    } finally {
      setAvatarUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleAvatarRemove = async () => {
    if (!window.confirm("Remove your profile photo?")) return;
    try {
      await axios.delete(`${API}/user/avatar`, { headers });
      setUser(prev => ({ ...prev, picture: null }));
      toast.success("Profile photo removed");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to remove photo");
    }
  };

  const startProfileEdit = () => {
    setProfileForm({ name: user?.name || "", phone: user?.phone || "" });
    setProfileEditing(true);
  };

  const handleProfileSave = async () => {
    if (!profileForm.name.trim()) { toast.error("Name is required"); return; }
    setProfileSaving(true);
    try {
      await axios.put(`${API}/user/profile`, {
        name: profileForm.name.trim(),
        phone: profileForm.phone.trim(),
      }, { headers });
      setUser(prev => ({ ...prev, name: profileForm.name.trim(), phone: profileForm.phone.trim() || null }));
      setProfileEditing(false);
      toast.success("Profile updated!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update profile");
    } finally {
      setProfileSaving(false);
    }
  };

  const initials = (user?.name || user?.email || "?").charAt(0).toUpperCase();

  const toggleEmailPref = async (key) => {
    const updated = { ...emailPrefs, [key]: !emailPrefs[key] };
    setEmailPrefs(updated);
    try {
      await axios.put(`${API}/user/email-preferences`, updated, { headers });
    } catch {
      setEmailPrefs(emailPrefs);
      toast.error("Failed to update preference");
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 pt-24 pb-16 px-4" data-testid="my-devices-page">
      <div className="max-w-2xl mx-auto">
        <Link to="/dashboard" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-6 transition-colors" data-testid="back-to-dashboard">
          <ArrowLeft className="w-4 h-4" />
          Back to Dashboard
        </Link>

        {/* ── Profile Picture Section ── */}
        <Card className="rounded-2xl border-0 shadow-lg overflow-hidden mb-6" data-testid="profile-card">
          <CardContent className="p-6">
            <div className="flex items-center gap-5">
              {/* Avatar with upload overlay */}
              <div className="relative group shrink-0" data-testid="avatar-wrapper">
                <div className="w-20 h-20 rounded-full overflow-hidden bg-gradient-to-br from-slate-200 to-slate-300 flex items-center justify-center ring-4 ring-white shadow-md">
                  <img
                    src={resolveImageUrl(user?.picture) || "/default-avatar.png"}
                    alt=""
                    className="w-full h-full object-cover"
                    referrerPolicy="no-referrer"
                    onError={(e) => { e.target.onerror = null; e.target.src = "/default-avatar.png"; }}
                    data-testid="avatar-image"
                  />
                </div>
                <button
                  onClick={() => fileInputRef.current?.click()}
                  disabled={avatarUploading}
                  className="absolute inset-0 rounded-full bg-black/0 group-hover:bg-black/40 flex items-center justify-center transition-all cursor-pointer"
                  data-testid="avatar-upload-btn"
                >
                  <span className="opacity-0 group-hover:opacity-100 transition-opacity">
                    {avatarUploading ? (
                      <Loader2 className="w-5 h-5 text-white animate-spin" />
                    ) : (
                      <Camera className="w-5 h-5 text-white" />
                    )}
                  </span>
                </button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp,image/gif"
                  className="hidden"
                  onChange={handleAvatarUpload}
                  data-testid="avatar-file-input"
                />
              </div>

              {/* User info */}
              <div className="flex-1 min-w-0">
                {profileEditing ? (
                  <div className="space-y-2.5" data-testid="profile-edit-form">
                    <div>
                      <label className="text-[11px] font-medium text-gray-500 uppercase tracking-wide">Name</label>
                      <Input
                        value={profileForm.name}
                        onChange={(e) => setProfileForm(f => ({ ...f, name: e.target.value }))}
                        placeholder="Your name"
                        className="h-9 text-sm mt-0.5"
                        data-testid="profile-edit-name"
                      />
                    </div>
                    <div>
                      <label className="text-[11px] font-medium text-gray-500 uppercase tracking-wide">Phone</label>
                      <Input
                        value={profileForm.phone}
                        onChange={(e) => setProfileForm(f => ({ ...f, phone: e.target.value }))}
                        placeholder="e.g. +233 XX XXX XXXX"
                        className="h-9 text-sm mt-0.5"
                        data-testid="profile-edit-phone"
                      />
                    </div>
                    <div className="flex items-center gap-2 pt-1">
                      <Button size="sm" className="h-8 px-4 text-xs" onClick={handleProfileSave} disabled={profileSaving} data-testid="profile-save-btn">
                        {profileSaving ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : <Check className="w-3.5 h-3.5 mr-1.5" />}
                        Save
                      </Button>
                      <Button size="sm" variant="ghost" className="h-8 px-3 text-xs" onClick={() => setProfileEditing(false)} data-testid="profile-cancel-btn">
                        Cancel
                      </Button>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="flex items-center gap-2">
                      <h2 className="text-lg font-bold text-gray-900 truncate" data-testid="profile-name">{user?.name || "User"}</h2>
                      <button onClick={startProfileEdit} className="text-gray-400 hover:text-gray-600 transition-colors" data-testid="profile-edit-btn" title="Edit profile">
                        <Pencil className="w-3.5 h-3.5" />
                      </button>
                    </div>
                    <p className="text-sm text-muted-foreground truncate">{user?.email}</p>
                    {user?.phone && <p className="text-sm text-muted-foreground">{user.phone}</p>}
                    <p className="text-xs text-muted-foreground mt-1 capitalize">{(user?.user_type || "").replace("_", " ")}</p>
                    <div className="flex items-center gap-3 mt-2">
                      <button
                        onClick={() => fileInputRef.current?.click()}
                        disabled={avatarUploading}
                        className="text-xs font-medium text-primary hover:underline"
                        data-testid="change-photo-link"
                      >
                        {avatarUploading ? "Uploading..." : user?.picture ? "Change photo" : "Add profile photo"}
                      </button>
                      {user?.picture && (
                        <button
                          onClick={handleAvatarRemove}
                          className="text-xs font-medium text-red-500 hover:underline"
                          data-testid="remove-photo-link"
                        >
                          Remove photo
                        </button>
                      )}
                    </div>
                  </>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* ── Email Notification Preferences ── */}
        <Card className="rounded-2xl border-0 shadow-lg overflow-hidden mb-6" data-testid="email-prefs-card">
          <CardContent className="p-5">
            <h3 className="font-semibold text-sm text-gray-900 mb-4 flex items-center gap-2">
              <Mail className="w-4 h-4 text-gray-500" />
              Email Notifications
            </h3>
            <div className="space-y-4">
              {[
                { key: "order_updates", label: "Order Updates", desc: "Get notified when your order status changes or downloads are ready", icon: Bell },
                { key: "promotional", label: "Promotions & Newsletters", desc: "Receive special offers, new product alerts, and educational tips", icon: Mail },
                { key: "quiz_results", label: "Quiz Results & Progress", desc: "Weekly progress summaries and performance insights from mock exams", icon: BarChart3 },
              ].map(({ key, label, desc, icon: Icon }) => (
                <div key={key} className="flex items-start justify-between gap-3" data-testid={`email-pref-${key}`}>
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-lg bg-gray-100 flex items-center justify-center shrink-0 mt-0.5">
                      <Icon className="w-4 h-4 text-gray-500" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-900">{label}</p>
                      <p className="text-xs text-muted-foreground mt-0.5">{desc}</p>
                    </div>
                  </div>
                  <Switch
                    checked={emailPrefs[key]}
                    onCheckedChange={() => toggleEmailPref(key)}
                    data-testid={`email-toggle-${key}`}
                  />
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* ── Devices Section ── */}
        <Card className="rounded-2xl border-0 shadow-lg overflow-hidden" data-testid="devices-card">
          <CardHeader className="bg-gradient-to-r from-slate-900 to-slate-800 text-white pb-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center">
                <Shield className="w-5 h-5" />
              </div>
              <div>
                <CardTitle className="text-lg font-bold">My Devices</CardTitle>
                <p className="text-sm text-slate-300 mt-0.5">
                  {devices.length} device{devices.length !== 1 ? "s" : ""} registered
                </p>
              </div>
            </div>
          </CardHeader>

          <CardContent className="p-0">
            {loading ? (
              <div className="flex justify-center py-16">
                <Loader2 className="w-7 h-7 animate-spin text-gray-400" />
              </div>
            ) : devices.length === 0 ? (
              <div className="text-center py-16 px-4">
                <Globe className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                <p className="text-sm text-muted-foreground">No devices registered yet. Devices are tracked when you log in.</p>
              </div>
            ) : (
              <div className="divide-y divide-gray-100">
                {devices.map((device) => {
                  const Icon = getDeviceIcon(device.browser, device.os);
                  const isEditing = editingId === device.id;
                  const isLoading = actionLoading === device.id;

                  return (
                    <div key={device.id} className="p-4 sm:p-5 hover:bg-gray-50/50 transition-colors" data-testid={`device-row-${device.id}`}>
                      <div className="flex items-start gap-3">
                        <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 mt-0.5 ${
                          device.is_primary ? "bg-blue-50 text-blue-600" :
                          !device.is_verified ? "bg-red-50 text-red-500" :
                          "bg-gray-100 text-gray-500"
                        }`}>
                          <Icon className="w-5 h-5" />
                        </div>

                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            {isEditing ? (
                              <div className="flex items-center gap-1.5 flex-1 min-w-0">
                                <Input
                                  value={editName}
                                  onChange={(e) => setEditName(e.target.value)}
                                  placeholder="e.g. My Office PC"
                                  className="h-8 text-sm rounded-lg max-w-[200px]"
                                  maxLength={50}
                                  autoFocus
                                  onKeyDown={(e) => { if (e.key === "Enter") handleRename(device.id); if (e.key === "Escape") setEditingId(null); }}
                                  data-testid={`rename-input-${device.id}`}
                                />
                                <Button size="icon" variant="ghost" className="h-7 w-7 text-emerald-600 hover:bg-emerald-50" onClick={() => handleRename(device.id)} disabled={isLoading} data-testid={`rename-save-${device.id}`}>
                                  {isLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
                                </Button>
                                <Button size="icon" variant="ghost" className="h-7 w-7 text-gray-400 hover:bg-gray-100" onClick={() => setEditingId(null)} data-testid={`rename-cancel-${device.id}`}>
                                  <X className="w-3.5 h-3.5" />
                                </Button>
                              </div>
                            ) : (
                              <>
                                <p className="font-medium text-sm text-gray-900 truncate" data-testid={`device-name-${device.id}`}>
                                  {device.nickname || device.browser || "Unknown Device"}
                                </p>
                                {device.is_primary && (
                                  <Badge className="bg-blue-100 text-blue-700 border-0 text-[10px] px-1.5 py-0 font-semibold" data-testid={`primary-badge-${device.id}`}>Primary</Badge>
                                )}
                                {!device.is_verified && (
                                  <Badge className="bg-red-100 text-red-700 border-0 text-[10px] px-1.5 py-0 font-semibold">Blocked</Badge>
                                )}
                              </>
                            )}
                          </div>

                          {device.nickname && !isEditing && (
                            <p className="text-xs text-muted-foreground mt-0.5">{device.browser} · {device.os}</p>
                          )}
                          {!device.nickname && !isEditing && (
                            <p className="text-xs text-muted-foreground mt-0.5">{device.os} · {device.screen_size}</p>
                          )}

                          <div className="flex items-center gap-1 mt-1.5 text-[11px] text-muted-foreground">
                            <Clock className="w-3 h-3" />
                            Last seen {formatDate(device.last_seen)}
                          </div>
                        </div>

                        {!isEditing && (
                          <div className="flex items-center gap-1 shrink-0">
                            <Button size="icon" variant="ghost" className="h-8 w-8 text-gray-400 hover:text-gray-700 hover:bg-gray-100" onClick={() => startEditing(device)} data-testid={`rename-btn-${device.id}`} title="Rename">
                              <Pencil className="w-3.5 h-3.5" />
                            </Button>
                            {!device.is_primary && (
                              <Button size="icon" variant="ghost" className="h-8 w-8 text-gray-400 hover:text-red-600 hover:bg-red-50" onClick={() => handleRemove(device)} disabled={isLoading} data-testid={`remove-btn-${device.id}`} title="Remove device">
                                {isLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
                              </Button>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            <div className="px-5 py-4 bg-gray-50 border-t border-gray-100">
              <div className="flex items-start gap-2 text-xs text-muted-foreground">
                <Shield className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                <p>
                  For your security, accounts are limited to 5 devices. Your primary device cannot be removed.
                  If you reach the limit, new devices will require email verification.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
